/**
 * Shared attribution + client-context plumbing for analytics.
 *
 * Client-side conversion captures (funnel completions, chatbot leads) fire
 * SERVER-SIDE via posthog-node, which has no access to the browser's posthog-js
 * super properties (traffic_source / utm_* / gclid) or its session id. This
 * module is the single source of truth for the keys we forward client→server
 * AND the trust boundary that sanitizes the (untrusted) client-supplied bag
 * before it lands on a server-side event.
 *
 * Safe to import from BOTH client and server code — no client/server-only deps.
 */

import { z } from "zod";

/**
 * Session-level (last-touch) attribution super properties. These are registered
 * by instrumentation-client.ts on each page load and read back via
 * posthog.get_property() at submit time.
 */
export const SESSION_ATTRIBUTION_KEYS = [
  "traffic_source",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "referring_domain",
  "landing_page",
] as const;

/**
 * First-touch (acquisition) attribution — set once at the first ever touch and
 * durable across sessions. This is the correct signal for CAC / "which ad
 * acquired this user", since conversions often complete in a later session than
 * the UTM/gclid-tagged landing.
 */
export const FIRST_TOUCH_ATTRIBUTION_KEYS = [
  "initial_traffic_source",
  "initial_utm_source",
  "initial_utm_medium",
  "initial_utm_campaign",
  "initial_utm_term",
  "initial_utm_content",
  "initial_gclid",
  "initial_landing_page",
] as const;

export const ATTRIBUTION_KEYS = [
  ...SESSION_ATTRIBUTION_KEYS,
  ...FIRST_TOUCH_ATTRIBUTION_KEYS,
] as const;

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];

export type AttributionProperties = Partial<Record<AttributionKey, string>>;

/**
 * Client PostHog context forwarded to server-side conversion captures alongside
 * the existing `posthog_distinct_id`:
 * - `attribution` restores last-touch + first-touch acquisition on the event.
 * - `current_url` carries the real funnel URL (overrides trackServerEvent's
 *   synthetic `https://host/`).
 * - `session_id` links the event to its session recording, so "View recording"
 *   works on server-side conversions (posthog-js auto-stamps this only on
 *   client events).
 */
export interface PostHogContextPayload {
  session_id?: string;
  current_url?: string;
  attribution?: AttributionProperties;
}

const MAX_VALUE_LEN = 512;
const MAX_SESSION_ID_LEN = 128;
const MAX_URL_LEN = 2048;

function cleanString(value: unknown, maxLen: number): string | undefined {
  return typeof value === "string" && value.length > 0 && value.length <= maxLen
    ? value
    : undefined;
}

/**
 * Whitelist + validate a client-supplied PostHog context bag before it rides a
 * server-side event. Client data is untrusted: only known attribution keys are
 * kept (iterating the fixed ATTRIBUTION_KEYS list, never the caller's keys, so
 * arbitrary/prototype keys can't leak through), every value must be a non-empty
 * string within length caps, and `session_id` / `current_url` are validated +
 * capped. Returns an always-defined `attribution` object plus optional ids.
 */
export function sanitizePostHogContext(input: unknown): {
  attribution: AttributionProperties;
  session_id?: string;
  current_url?: string;
} {
  const attribution: AttributionProperties = {};
  if (!input || typeof input !== "object") {
    return { attribution };
  }

  const ctx = input as Record<string, unknown>;
  const rawAttribution =
    ctx.attribution && typeof ctx.attribution === "object"
      ? (ctx.attribution as Record<string, unknown>)
      : {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = cleanString(rawAttribution[key], MAX_VALUE_LEN);
    if (value !== undefined) attribution[key] = value;
  }

  return {
    attribution,
    session_id: cleanString(ctx.session_id, MAX_SESSION_ID_LEN),
    current_url: cleanString(ctx.current_url, MAX_URL_LEN),
  };
}

/**
 * Build the PostHog event-property bag for a server-side conversion capture
 * from the (untrusted) client `posthog_context`: sanitized attribution +
 * `$session_id` (links the event to its session recording) + `$current_url`
 * (the real funnel URL, overriding trackServerEvent's synthetic one). Spread
 * the result into a capture's props. Shared by the funnel routes so the trust
 * boundary and the `$`-prefix mapping can't drift across them.
 */
export function conversionContextProps(input: unknown): Record<string, string> {
  const { attribution, session_id, current_url } = sanitizePostHogContext(input);
  return {
    ...attribution,
    ...(session_id ? { $session_id: session_id } : {}),
    ...(current_url ? { $current_url: current_url } : {}),
  };
}

/**
 * Zod schema for the sanitized PostHog context bundle as it rides a domain-event
 * payload (`request.created` / `offering.created` / `contact.submitted` /
 * `land_check.submitted`). Matches {@link PostHogContextPayload}. Values are
 * already whitelisted + length-capped by {@link toStoredPostHogContext} at the
 * ingest boundary, so this stays permissive (a record of strings): its job is to
 * let the field round-trip through the event schema, not to re-enforce the
 * whitelist. `.optional()` so pre-existing events and non-funnel publishers
 * (e.g. OPaaS request.created) validate without it.
 */
export const PostHogContextSchema = z
  .object({
    session_id: z.string().optional(),
    current_url: z.string().optional(),
    attribution: z.record(z.string(), z.string()).optional(),
  })
  .optional();

/**
 * Reduce an untrusted client `posthog_context` to the bundle we PERSIST on a
 * lead entity (requests / offerings / land_check_submissions), so the deferred,
 * out-of-request-scope canonical `<entity>.created` / `.submitted` event can
 * still carry the funnel's session id + acquisition attribution. This mirrors
 * how `creation_source` is persisted on the row for the same reason: for guests
 * the canonical event is published much later (at email verification / the OTP
 * sweep / an in-app claim), where the browser context is long gone.
 *
 * Sanitizes via {@link sanitizePostHogContext} (whitelist + length caps — the
 * trust boundary) and returns `null` when there is nothing worth keeping, so
 * callers store SQL NULL rather than an empty `{}`.
 */
export function toStoredPostHogContext(input: unknown): PostHogContextPayload | null {
  const { attribution, session_id, current_url } = sanitizePostHogContext(input);
  const hasAttribution = Object.keys(attribution).length > 0;
  if (!hasAttribution && !session_id && !current_url) return null;
  return {
    ...(session_id ? { session_id } : {}),
    ...(current_url ? { current_url } : {}),
    ...(hasAttribution ? { attribution } : {}),
  };
}
